import { createSelector } from '@reduxjs/toolkit';
import { t } from 'i18next';
import isNil from 'lodash-es/isNil';
import pick from 'lodash-es/pick';
import uniq from 'lodash-es/uniq';
import { v4 as uuidv4 } from 'uuid';

import {
  LEAD_DATA_VARIABLES,
  SYS_CONTEXT_VARIABLES,
  USER_INFO_VARIABLES,
} from '@/redux/dialogs/helper';
import {
  Action,
  CarouselAction,
  QuestionSetAction,
  ResetActionOption,
  TextAction,
  WhatsAppFlowAction,
} from '@/shared/types/action';
import {
  ContextVariableOption,
  LeadDataVariableOption,
  SystemVariableOption,
  UserInfoVariableOption,
} from '@/shared/types/autocomplete';
import { Dialog, PartialDialog } from '@/shared/types/dialog';
import {
  AuthStepKey,
  Condition,
  ConditionMatchOption,
  Node,
  Nodes,
  Requisite,
} from '@/shared/types/node';
import { RootState } from '@/shared/types/state';
import { Webhook } from '@/shared/types/webhook';
import { checkForDollar } from '@/util/util';

import { findAction, findActionContainerById } from '../nodes/actions';

/** Interface representing a tree item in the dialog structure */
export interface TreeItem {
  name: string;
  id: string;
  collection: string;
  link: string;
}

// An auth sub_agent's success/failure targets, plus which step's outcome box
// to open when deep-linking to them. success_node_id/failure_node_id are
// config-level, so we point at the final relevant step (verification when
// enabled, else identification).
type AuthTargets = { success_node_id?: string; failure_node_id?: string; step: AuthStepKey };

/** Type representing all actions across dialogs including their metadata */
type AllActionsType = {
  action_id: string;
  type: string;
  tags?: string[];
  node_id: string;
  dialog_id: string;
  requisite_idx: number | null;
  dialog_name: string;
  if_no_requisite?: true;
  trigger_node_id?: string | null;
  // Present on auth sub_agent actions — the success/failure target nodes (so the
  // delete-node warning can flag them) plus where to deep-link (the exact
  // success/failure dropdown). The auth analogue of an event's trigger_node_id.
  auth_targets?: AuthTargets;
}[];

const getAuthTargets = (action: Action): AuthTargets | undefined => {
  if (action.type !== 'sub_agent' || action.sub_agent_config?.type !== 'auth') {
    return undefined;
  }
  const config = action.sub_agent_config;
  return {
    success_node_id: config.success_node_id,
    failure_node_id: config.failure_node_id,
    step: config.verification_step?.enabled ? 'verification' : 'identification',
  };
};

/**
 * Selects the draft dialog from the state
 * @param state - The root state
 * @returns The partial dialog object containing dialog_id, nodes and name
 */
export const selectDraftDialog: (state: RootState) => PartialDialog = createSelector(
  (state: RootState) => state.nodes,
  (nodes) => {
    return { dialog_id: nodes.dialogId, nodes: nodes.nodes, name: nodes.dialogName };
  }
);

/**
 * Selects the draft dialog ID from the state
 * @param state - The root state
 * @returns The dialog ID string
 */
export const selectDraftDialogId: (state: RootState) => string = createSelector(
  (state: RootState) => state.nodes,
  (nodes) => nodes.dialogId
);

/**
 * Selects all nodes from the draft dialog
 * @param state - The root state
 * @returns Array of nodes in the draft dialog
 */
export const selectDraftDialogNodes = (state: RootState) => state.nodes.nodes ?? [];

/**
 * Selects all actions from the draft dialog nodes
 * @param state - The root state
 * @returns Array of all actions across all nodes
 */
export const selectDrafDialogActions = createSelector(
  (state: RootState) => state.nodes,
  (nodes) => {
    return (nodes.nodes ?? []).reduce((acc, node) => {
      return [...acc, ...(node.actions || [])];
    }, [] as Action[]);
  }
);

/**
 * Selects the dialog ID from the state
 * @param state - The root state
 * @returns The dialog ID string
 */
export const selectDialogId: (state: RootState) => string = createSelector(
  (state: RootState) => state.nodes,
  (nodes) => nodes.dialogId
);

/**
 * Finds and returns a node containing the specified action ID
 * @param state - The root state
 * @param actionId - The ID of the action to find
 * @returns The node containing the action, or null if not found
 */
export const selectNodeByActionId: (state: RootState, actionId: string) => Node | null =
  createSelector(
    [(state: RootState) => state.nodes.nodes, (_: RootState, actionId: string) => actionId],
    (nodes, actionId) => {
      // Find action in nodes
      const n = nodes.find(({ actions }) => findAction(actions, actionId));
      if (n) {
        return n;
      }
      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];

        // Find action in conditions
        const condition = (node.conditions || []).find(({ actions }) =>
          findAction(actions, actionId)
        );
        if (condition) {
          return node;
        }
        // Find action in requisites
        const requisite = (node.requisites || []).find(({ actions }) =>
          findAction(actions || [], actionId)
        );
        if (requisite) {
          return node;
        }
      }
      return null;
    }
  );

/**
 * Selects a node by its ID
 * @param state - The root state
 * @param nodeId - The ID of the node to find
 * @returns The node with the specified ID, or null if not found
 */
export const selectNodeById: (state: RootState, nodeId: string) => Node | null = createSelector(
  (state: RootState) => state.nodes.nodes,
  (_: RootState, nodeId: string) => nodeId,
  (nodes, nodeId) => nodes.find((n) => n.node_id === nodeId)
);

/**
 * Selects requisites for a given node
 * @param state - The root state
 * @returns Array of requisites for the selected node
 */
export const selectRequisites = createSelector(selectNodeById, (node) => node?.requisites);

/**
 * Selects the no-requisites flag for a given node
 * @param state - The root state
 * @returns The if_no_requisites value for the selected node
 */
export const selectNoRequisites = createSelector(selectNodeById, (node) => node?.if_no_requisites);

/**
 * Selects the intent for a given node ID
 * @param state - The root state
 * @param nodeId - The ID of the node
 * @returns The intent string or null if not found
 */
export const selectIntentByNodeId: (state: RootState, nodeId: string) => string | null =
  createSelector(
    (state: RootState) => state.nodes.nodes,
    (_: RootState, nodeId: string) => nodeId,
    (nodes, nodeId) => {
      const node = nodes.find((n) => n.node_id === nodeId);
      return node ? node.intent : null;
    }
  );

/**
 * Selects requisites for a given node ID
 * @param state - The root state
 * @param nodeId - The node ID to get requisites for
 * @returns Array of requisites for the specified node
 */
export const selectRequisitesByNodeId = createSelector(
  (state: RootState) => state.nodes.nodes,
  (_: RootState, nodeId: string) => nodeId,
  (nodes: Node[], nodeId) => nodes.find((n) => n.node_id === nodeId)?.requisites
);

/**
 * Selects conditions for a given node ID
 * @param state - The root state
 * @param nodeId - The node ID to get conditions for
 * @returns Array of conditions for the specified node
 */
export const selectConditionsByNodeId: (
  state: RootState,
  nodeId: string
) => Condition[] | undefined = createSelector(
  (state: RootState) => state.nodes.nodes,
  (_: RootState, nodeId: string) => nodeId,
  (nodes: Node[], nodeId) => nodes.find((n) => n.node_id === nodeId)?.conditions
);

/**
 * Gets the number of conditions for a given node ID
 * @param state - The root state
 * @param nodeId - The node ID to count conditions for
 * @returns The number of conditions
 */
export const selectConditionsByNodeIdLength: (state: RootState, nodeId: string) => number =
  createSelector(
    (state: RootState) => state.nodes.nodes,
    (_: RootState, nodeId: string) => nodeId,
    (nodes: Node[], nodeId) => nodes.find((n) => n.node_id === nodeId)?.conditions?.length || 0
  );

/**
 * Selects actions based on node ID, condition index, and requisite index
 * @param state - The root state
 * @param props - Object containing nodeId, conditionIndex, and requisiteIndex
 * @returns Array of actions for the specified container
 */
export const selectActions = createSelector(
  (state: RootState) => state.nodes.nodes,
  (_: RootState, props: { nodeId: string; conditionIndex: number; requisiteIndex: number }) =>
    props,
  (nodes, { nodeId, conditionIndex, requisiteIndex }) => {
    let actionContainer: Node | Condition | Requisite | undefined = nodes.find(
      (n) => n.node_id === nodeId
    );
    if (!isNil(conditionIndex) && actionContainer?.conditions) {
      actionContainer = actionContainer?.conditions[conditionIndex];
    } else if (!isNil(requisiteIndex)) {
      actionContainer = actionContainer?.requisites[requisiteIndex];
    }
    return actionContainer?.actions || [];
  }
);

/**
 * Returns true if the draft dialog already contains a webview action (or carousel
 * webview button) with auto_open enabled, excluding the action with `excludedActionId`.
 * For carousel buttons, pass `excludedCardIndex` and `excludedButtonIndex` to skip the
 * specific button being edited so it is not counted against itself.
 */
export const selectHasOtherAutoOpenWebview = (
  state: RootState,
  excludedActionId: string,
  excludedCardIndex?: number,
  excludedButtonIndex?: number
): boolean => {
  const hasExcludedButton = excludedCardIndex != null && excludedButtonIndex != null;

  const carouselHasAutoOpen = (action: CarouselAction, isExcludedCarousel: boolean) =>
    action.cards?.some((card, cardIdx) => {
      if (card.default_action?.type === 'webview' && card.default_action.auto_open) {
        return true;
      }
      return card.buttons?.some((btn, btnIdx) => {
        if (
          isExcludedCarousel &&
          hasExcludedButton &&
          excludedCardIndex === cardIdx &&
          excludedButtonIndex === btnIdx
        ) {
          return false;
        }
        return btn.type === 'webview' && btn.auto_open;
      });
    });

  const actionsHaveAutoOpen = (actions: Action[] | undefined) =>
    !!actions?.some((action) => {
      const isExcluded = action.action_id === excludedActionId;
      if (action.type === 'webview') {
        return !isExcluded && !!action.auto_open;
      }
      if (action.type === 'carousel') {
        return carouselHasAutoOpen(action, isExcluded);
      }
      return false;
    });

  return state.nodes.nodes.some(
    (node) =>
      actionsHaveAutoOpen(node.actions) ||
      node.conditions?.some((c) => actionsHaveAutoOpen(c.actions)) ||
      node.requisites?.some((r) => actionsHaveAutoOpen(r.actions))
  );
};

/**
 * Selects external actions for requisites of a given node
 * @param state - The root state
 * @param props - Object containing nodeId and indices
 * @returns Array of question type actions for requisites
 */
export const selectRequisiteExternalActions = createSelector(
  (state: RootState) => state.nodes.nodes,
  (_: RootState, props: { nodeId: string; conditionIndex: number; requisiteIndex: number }) =>
    props,
  (nodes, { nodeId }) => {
    const actionContainer: Node | Condition | Requisite | undefined = nodes.find(
      (n) => n.node_id === nodeId
    );

    if (actionContainer?.requisites?.length > 0) {
      const requisitesActions = actionContainer?.requisites.map(() => ({
        type: 'question',
        action_id: uuidv4(),
      }));

      return requisitesActions;
    }
    return [];
  }
);

/** The node an action continues to: a jump, or a question set after its answers. */
export const jumpTargetOf = (action: Action): string | null => {
  if (action.type === 'event' || action.type === 'question_set') {
    return action.trigger_node_id ?? null;
  }
  return null;
};

/**
 * Gets all actions from all dialogs including their conditions and requisites
 * @param dialogs - Array of dialogs to extract actions from
 * @returns Array of all actions
 */
export const getActionsSelector: (dialogs: Dialog[]) => AllActionsType = (dialogs) => {
  const allActions = [];
  if (!dialogs) {
    return;
  }

  // Get all the actions from all the dialogs
  for (let d = 0; d < dialogs.length; d += 1) {
    for (let n = 0; n < dialogs[d].nodes.length; n += 1) {
      for (let a = 0; a < dialogs[d].nodes[n].actions?.length; a += 1) {
        const action = dialogs[d].nodes[n].actions[a];

        const tags = action.type === 'tag' ? action.tags : undefined;
        const trigger_node_id = jumpTargetOf(action);

        allActions.push({
          action_id: dialogs[d].nodes[n].actions[a].action_id,
          type: dialogs[d].nodes[n].actions[a].type,
          tags,
          node_id: dialogs[d].nodes[n].node_id,
          dialog_id: dialogs[d].dialog_id,
          requisite_idx: null,
          trigger_node_id,
          auth_targets: getAuthTargets(action),
          dialog_name: dialogs[d].name,
        });
      }

      // Get all the actions from all the conditions
      const conditions = dialogs[d]?.nodes[n]?.conditions;
      if (conditions && conditions.length) {
        for (let c = 0; c < conditions?.length; c += 1) {
          for (let a = 0; a < conditions[c].actions?.length; a += 1) {
            const action = conditions[c].actions[a];
            const trigger_node_id = jumpTargetOf(action);
            const tags = action.type === 'tag' ? action.tags : undefined;
            allActions.push({
              action_id: conditions[c].actions[a].action_id,
              type: conditions[c].actions[a].type,
              tags,
              node_id: dialogs[d].nodes[n].node_id,
              dialog_id: dialogs[d].dialog_id,
              requisite_idx: null,
              trigger_node_id,
              auth_targets: getAuthTargets(action),
              dialog_name: dialogs[d].name,
            });
          }
        }
      }

      // Get all the actions from all the requisites
      const requisites = dialogs[d]?.nodes[n]?.requisites;
      if (requisites && requisites.length) {
        for (let r = 0; r < dialogs[d].nodes[n].requisites?.length; r += 1) {
          for (let a = 0; a < requisites[r].actions?.length; a += 1) {
            allActions.push({
              ...pick(requisites[r].actions[a], ['action_id', 'type', 'tags']),
              node_id: dialogs[d].nodes[n].node_id,
              dialog_id: dialogs[d].dialog_id,
              requisite_idx: r,
              dialog_name: dialogs[d].name,
            });
          }
        }
      }

      const if_no_requisites = dialogs[d]?.nodes[n]?.if_no_requisites;
      if (if_no_requisites && if_no_requisites.length) {
        for (let r = 0; r < dialogs[d].nodes[n]?.if_no_requisites?.length; r += 1) {
          allActions.push({
            action_id: if_no_requisites[r].action_id,
            type: if_no_requisites[r].type,
            tags: null,
            node_id: dialogs[d].nodes[n].node_id,
            dialog_id: dialogs[d].dialog_id,
            requisite_idx: null,
            if_no_requisite: true,
            dialog_name: dialogs[d].name,
          });
        }
      }
    }
  }
  return allActions;
};

/**
 * Selects branches (child nodes) for a given node ID
 * @param state - The root state
 * @param nodeId - The parent node ID
 * @returns Array of node IDs that are children of the specified node
 */
export const selectBranchesByNodeId = createSelector(
  (state: RootState): Node[] => state.nodes.nodes,
  (_: RootState, nodeId: string) => nodeId,
  // An auth sub_agent action's success/failure targets are plain node
  // references (like an event/jump target), not children of the action — they
  // render in their own place in the tree, so they must NOT be filtered out of
  // their real parent's branches (doing so made them disappear). The legacy
  // node-based auth auto-created those targets as child nodes and hid them
  // here; that no longer applies.
  (nodes, nodeId): string[] => nodes.filter((n) => n.parent_id === nodeId).map((n) => n.node_id)
);

/**
 * Selects the currently selected node from the state
 * @param state - The root state
 * @returns The selected node or undefined if none selected
 */
export const selectSelectedNode = createSelector(
  (state: RootState) => state.nodes,
  ({ selectedNodeId, nodes }: Nodes) => nodes.find(({ node_id }) => node_id === selectedNodeId)
);

/**
 * Selects the currently selected action
 * @param state - The root state
 * @returns The selected action or null/undefined if none selected
 */
export const selectSelectedAction: (state: RootState) => Action | null | undefined = createSelector(
  ({ nodes }: { nodes: Nodes }) => nodes,
  ({ nodes, selectedActionId }: Nodes) => {
    if (selectedActionId) {
      const container: { actions: Action[] } = findActionContainerById(nodes, selectedActionId);
      if (container) {
        return container.actions.find((a) => a.action_id === selectedActionId);
      }
    }
    return null;
  }
);

/**
 * Selects the WhatsApp Flow action if the selected action is of that type
 * @param state - The root state
 * @returns The WhatsApp Flow action or null if not applicable
 */
export const selectWhatsAppFlowAction = createSelector(
  [selectSelectedAction],
  (action): WhatsAppFlowAction | null => {
    if (action?.type === 'whatsapp_flow') {
      return action as WhatsAppFlowAction;
    }
    return null;
  }
);

/**
 * Selects the node ID for the currently selected WhatsApp Flow action
 * @param state - The root state
 * @returns The node ID or empty string if not found
 */
export const selectNodeIdByWhatsAppFlowAction = createSelector(
  [(state: RootState) => state, selectWhatsAppFlowAction],
  (state, action): string => {
    if (!action) {
      return '';
    }
    return selectNodeByActionId(state, action.action_id)?.node_id ?? '';
  }
);

/**
 * Selects the question set action if the selected action is of that type
 * @param state - The root state
 * @returns The question set action or null if not applicable
 */
export const selectQuestionSetAction = createSelector(
  [selectSelectedAction],
  (action): QuestionSetAction | null => {
    if (action?.type === 'question_set') {
      return action;
    }
    return null;
  }
);

/**
 * Selects carousel action data from the currently selected action
 * @param state - The root state
 * @returns Object containing carousel action data
 */
export const selectCarouselActionData = createSelector([selectSelectedAction], (selectedAction) => {
  const carouselAction = selectedAction as CarouselAction;
  return { actionId: carouselAction?.action_id ?? '', cards: carouselAction?.cards ?? [] };
});

/**
 * Selects nodes for a specific dialog
 * @param state - The root state
 * @param dialogId - The dialog ID to get nodes for
 * @returns Array of nodes for the specified dialog
 */
export const selectNodes = createSelector(
  ({ nodes }: { nodes: Nodes }) => nodes,
  (_: RootState, dialogId: string) => dialogId,
  (nodes, dialogId): Node[] => {
    if (dialogId !== nodes.dialogId) {
      return [];
    }
    return nodes.nodes;
  }
);

/**
 * Checks if a node or action is currently selected
 * @param state - The root state
 * @returns Boolean indicating if there is a selection
 */
export const isNodeOrActionSelected = createSelector(
  (state: RootState) => state.nodes,
  ({ selectedActionId, selectedNodeId }): boolean => !!selectedActionId || !!selectedNodeId
);

/**
 * Selects the currently selected condition
 * @param state - The root state
 * @returns The selected condition or null if none selected
 */
export const selectSelectedCondition = createSelector(
  selectSelectedNode,
  (state: RootState) => state.nodes.selectedConditionIndex,
  (node, selectedConditionIndex) => {
    const conditions = node?.conditions;
    if (typeof selectedConditionIndex !== 'number') {
      return null;
    }
    if (!conditions || conditions.length < selectedConditionIndex) {
      return null;
    }
    return conditions[selectedConditionIndex];
  }
);

/**
 * Selects sibling conditions of the currently selected one — the other conditions on the
 * same node. Used as peer context when validating a guideline condition.
 */
export const selectSelectedConditionSiblings = createSelector(
  selectSelectedNode,
  (state: RootState) => state.nodes.selectedConditionIndex,
  (node, selectedConditionIndex): Condition[] => {
    const conditions = node?.conditions;
    if (!conditions || typeof selectedConditionIndex !== 'number') {
      return [];
    }
    return conditions.filter((_, index) => index !== selectedConditionIndex);
  }
);

const matchOptions: ConditionMatchOption[] = [
  { value: 'guideline', label_key: 'dialog.condition.guideline', disabled: false },
  { value: 'any', label_key: 'dialog.condition.match_any', disabled: false },
  { value: 'all', label_key: 'dialog.condition.match_all', disabled: false },
  { value: 'else', label_key: 'dialog.condition.match_else', disabled: false },
];

/**
 * Gets available match options for conditions. The option `else` there is another condition with the same match value.
 * @param state - The root state
 * @returns Array of condition match options with their availability status
 */
export const selectMatchOptions = createSelector(
  selectSelectedNode,
  (state: RootState) => state.nodes.selectedConditionIndex,
  (node, selectedConditionIndex): ConditionMatchOption[] => {
    const conditions = node?.conditions ?? [];
    const elseConditionIndex = conditions.findIndex((c) => c.match === 'else');

    const disableElse = elseConditionIndex !== -1 && elseConditionIndex !== selectedConditionIndex;
    const options = [...matchOptions];
    const elseOption = options.find((option) => option.value === 'else');
    if (elseOption) {
      elseOption.disabled = disableElse;
    }

    return options;
  }
);

/**
 * Selects the guideline rule from the selected condition. This will return the rule corresponding to a `guideline` condition.
 * @param state - The root state
 * @returns The guideline rule with its index if found, undefined otherwise
 */
export const selectGuidelineRule = createSelector(
  (state: RootState) => selectSelectedCondition(state),
  (condition) => {
    if (!condition?.rules) {
      return undefined;
    }

    const index = condition.rules.findIndex((rule) => rule.operator === 'guideline');

    if (index === -1) {
      return undefined;
    }

    return { ...condition.rules[index], index };
  }
);

/**
 * Selects condition toolkit data for the selected condition
 * @param state - The root state
 * @returns Object containing condition toolkit configuration
 */
export const selectConditionToolkit = createSelector(
  (state: RootState) => selectSelectedNode(state),
  (state: RootState) => selectMatchOptions(state),
  (state: RootState) => selectSelectedCondition(state),
  (state: RootState) => selectGuidelineRule(state),
  (state: RootState) => state.nodes.selectedConditionIndex,
  (node, matchOptions, condition, guidelineRule, conditionIndex) => {
    return {
      nodeId: node.node_id,
      conditionIndex,
      match: condition.match,
      name: condition.name,
      matchOptions,
      conditionId: condition.condition_id,
      guidelineRule,
    };
  }
);

/**
 * Selects the match value for the selected condition.
 * `Guideline` is a special value that is used to indicate that the condition is a guideline type.
 * This is when the `match` is set to `all` and the `rules` array contains a `guideline` rule.
 * @param state - The root state
 * @returns The match value
 */
export const selectMatchValue = createSelector(
  selectGuidelineRule,
  selectSelectedCondition,
  (guidelineRule, condition) => {
    if (guidelineRule && condition.match === 'all') {
      return 'guideline';
    }
    return condition.match;
  }
);

const resetOptions: ResetActionOption[] = [
  { value: 'all', label_key: 'dialog.reset.all_variables', disabled: false },
  { value: 'custom', label_key: 'dialog.reset.custom_variables', disabled: false },
];

/**
 * Gets available reset options
 * @param state - The root state
 * @returns Array of reset action options with their availability status
 */
export const selectResetOptions = createSelector(
  selectSelectedAction,
  (action): ResetActionOption[] => {
    const options = [...resetOptions];
    if (action?.type === 'reset') {
      const variables = action?.variables ?? [];
      // disable all variables option if there exists a reset variable
      if (variables.length > 0) {
        options[0].disabled = true;
      } else {
        options[0].disabled = false;
      }
    }
    return options;
  }
);

/**
 * Gets all unique tags from all dialogs
 * @param dialogs - Array of dialogs to extract tags from
 * @returns Array of unique tag strings
 */
export const selectAllTags = (dialogs: Dialog[]): string[] => {
  const actions = getActionsSelector(dialogs);
  const tags = actions
    ?.filter((a) => a.type === 'tag')
    .reduce((acc, val) => acc.concat(val.type === 'tag' ? val?.tags || [] : []), [] as string[]);
  return uniq(tags);
};

/**
 * Gets context variables from requisites across all dialogs
 * @param dialogs - Array of dialogs to extract variables from
 * @returns Array of context variable options
 */
export const selectContextVariablesFromRequisites = (
  dialogs: Partial<Dialog>[]
): ContextVariableOption[] => {
  if (!dialogs) {
    return [];
  }

  const contextVariables: ContextVariableOption[] = [];
  const contextVariablesSet = new Set<string>();
  for (let i = 0; i < dialogs.length; i += 1) {
    for (let j = 0; j < dialogs[i].nodes?.length; j += 1) {
      if (dialogs[i].nodes[j]?.requisites) {
        dialogs[i].nodes[j].requisites.forEach(
          (requisite: { save_as: string; check_for?: string }) => {
            const values = [];
            if (requisite?.save_as) {
              values.push(`$${requisite.save_as}`);
            }
            if (requisite?.check_for && requisite.check_for[0] === '$') {
              values.push(requisite.check_for);
            }
            if (requisite?.check_for && requisite?.save_as && requisite.check_for[0] === '@') {
              values.push(`$${requisite.save_as}_value`);
            }
            values.forEach((value) => {
              if (!contextVariablesSet.has(value)) {
                contextVariablesSet.add(value);
                contextVariables.push({ value: value, label: value, type: 'context_variable' });
              }
            });
          }
        );
      }
    }
  }
  return contextVariables;
};

/**
 * Gets context variables from actions across all dialogs
 * @param dialogs - Array of dialogs to extract variables from
 * @returns Array of context variable options
 */
export const selectContextVariablesFromActions = (
  dialogs: Partial<Dialog>[]
): ContextVariableOption[] => {
  if (!dialogs) {
    return [];
  }

  const contextVariables: ContextVariableOption[] = [];
  const contextVariableValues = new Set();

  const addVariable = (variable: string) => {
    const varValue = `$${variable}`;
    // Skip adding to contextVariables the $live_instructions variable,
    // because it will be added later as a special variable
    if (
      !contextVariableValues.has(varValue) &&
      varValue !== '$live_instructions' &&
      varValue !== '$tags'
    ) {
      contextVariableValues.add(varValue);
      contextVariables.push({
        value: checkForDollar(variable),
        label: checkForDollar(variable),
        type: 'context_variable',
      });
    }
  };

  const collect = (actions?: Action[]) => {
    actions?.forEach((action) => {
      if (action?.type === 'reset') {
        action.variables?.forEach(addVariable);
      }
      // Each answer fills the context variable named by the question key
      if (action?.type === 'question_set') {
        action.question_set?.questions?.forEach((question) => addVariable(question.key));
      }
    });
  };

  for (let i = 0; i < dialogs.length; i += 1) {
    for (let j = 0; j < dialogs[i].nodes?.length; j += 1) {
      const node = dialogs[i].nodes[j];
      collect(node?.actions);
      node?.conditions?.forEach((condition) => collect(condition.actions));
      node?.requisites?.forEach((requisite) => collect(requisite.actions));
    }
  }
  return contextVariables;
};

/**
 * Gets context variables from conditions across all dialogs
 * @param dialogs - Array of dialogs to extract variables from
 * @returns Array of context variable options
 */
export const selectContextVariablesFromConditions = (
  dialogs: Partial<Dialog>[]
): ContextVariableOption[] => {
  if (!dialogs) {
    return [];
  }

  const contextVariablesSet = new Set<string>();

  for (const dialog of dialogs) {
    for (const node of dialog?.nodes ?? []) {
      for (const condition of node?.conditions ?? []) {
        condition?.rules?.forEach((rule: { name: string }) => {
          if (rule?.name && rule.name[0] === '$') {
            contextVariablesSet.add(rule.name);
          }
        });

        condition?.actions?.forEach((action) => {
          if (action?.type === 'reset') {
            action.variables?.forEach((variable) => {
              contextVariablesSet.add(`$${variable}`);
            });
          }
        });
      }
    }
  }

  // Convert the set back to the desired array format.
  const contextVariables: ContextVariableOption[] = Array.from(contextVariablesSet).map((name) => ({
    value: name,
    label: name,
    type: 'context_variable',
  }));

  return contextVariables;
};

/**
 * Gets context variables from webhooks
 * @param webhooks - Array of webhooks to extract variables from
 * @returns Array of context variable options
 */
export const selectContextVariablesFromWebhooks = (
  webhooks: Webhook[]
): ContextVariableOption[] => {
  if (!webhooks) {
    return [];
  }

  const contextVariablesSet: Set<string> = new Set();

  for (const webhook of webhooks) {
    if (
      webhook?.test_schema &&
      typeof webhook.test_schema !== 'string' &&
      webhook.test_schema.context
    ) {
      for (const key of Object.keys(webhook.test_schema.context)) {
        if (!contextVariablesSet.has(key)) {
          contextVariablesSet.add(key);
        }
      }
    }
  }

  const contextVariables: ContextVariableOption[] = Array.from(contextVariablesSet).map((key) => ({
    value: checkForDollar(key),
    label: checkForDollar(key),
    type: 'context_variable',
  }));

  return contextVariables;
};

const uniqueByValue = <T extends { value: string }>(options: T[]): T[] => {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (seen.has(option.value)) {
      return false;
    }
    seen.add(option.value);
    return true;
  });
};

/**
 * Gets all non-system variables from dialogs and webhooks
 * @param dialogs - Array of dialogs
 * @param draftDialog - The draft dialog
 * @param webhooks - Array of webhooks
 * @returns Array of context variable options excluding system variables
 */
export const noSystemVariables = (
  dialogs: Dialog[],
  draftDialog: Partial<Dialog>,
  webhooks: Webhook[]
): ContextVariableOption[] => {
  const dialogsWithDraft = [...(dialogs ?? []), draftDialog];

  const requisites = selectContextVariablesFromRequisites(dialogsWithDraft);
  const actions = selectContextVariablesFromActions(dialogsWithDraft);
  const conditions = selectContextVariablesFromConditions(dialogsWithDraft);
  const webhooksContextVariables = selectContextVariablesFromWebhooks(webhooks);
  // These serve special occassions, like the live instructions
  const reservedContextVariables: ContextVariableOption[] = [
    {
      label: t('dialog.set_variables.live_instructions') || '$live_instructions',
      value: '$live_instructions',
      type: 'context_variable',
    },
  ];

  const uniqueVariables = uniqueByValue([
    ...reservedContextVariables,
    ...requisites,
    ...actions,
    ...conditions,
    ...webhooksContextVariables,
  ]);

  // Remove system variables from the list, because they can be contained in the conditions
  const nonSystemVariables = uniqueVariables.filter(
    (uniqueVariable) =>
      !SYS_CONTEXT_VARIABLES.some((systemVariable) => uniqueVariable.label === systemVariable)
  );
  return nonSystemVariables;
};

/**
 * Gets all custom variables (excluding system and user variables)
 * @param dialogs - Array of dialogs
 * @param draftDialog - The draft dialog
 * @param webhooks - Array of webhooks
 * @returns Array of custom context variable options
 */
export const selectCustomVariables = (
  dialogs: Dialog[],
  draftDialog: Partial<Dialog>,
  webhooks: Webhook[]
): ContextVariableOption[] => {
  return noSystemVariables(dialogs, draftDialog, webhooks).filter(
    ({ label }) => !label.startsWith('$user.')
  );
};

/**
 * Gets all variables including system, user info, and lead data variables
 * @param dialogs - Array of dialogs
 * @param draftDialog - The draft dialog
 * @param webhooks - Array of webhooks
 * @returns Array of all variable options
 */
export const selectAllVariables = (
  dialogs: Dialog[],
  draftDialog: Partial<Dialog>,
  webhooks: Webhook[]
): (
  ContextVariableOption | SystemVariableOption | UserInfoVariableOption | LeadDataVariableOption
)[] => {
  if (!dialogs) {
    return [];
  }

  const systemVariables: SystemVariableOption[] = SYS_CONTEXT_VARIABLES.map(
    (sysVariable: string) => ({
      label: t(`auto_complete.variable_labels.${sysVariable}`),
      value: sysVariable,
      type: 'system_variable',
    })
  );

  const userInfoVariables: UserInfoVariableOption[] = USER_INFO_VARIABLES.map(
    (userInfoVariable: string) => ({
      label: t(`auto_complete.variable_labels.${userInfoVariable}`),
      value: userInfoVariable,
      type: 'user_info_variable',
    })
  );

  const leadDataVariables: LeadDataVariableOption[] = LEAD_DATA_VARIABLES.map(
    (leadDataVariable: string) => ({
      label: t(`auto_complete.variable_labels.${leadDataVariable}`),
      value: leadDataVariable,
      type: 'lead_data_variable',
    })
  );

  const allVariables = [
    ...leadDataVariables,
    ...userInfoVariables,
    ...systemVariables,
    ...noSystemVariables(dialogs, draftDialog, webhooks),
  ];

  return uniqueByValue(allVariables);
};

export const selectSelectedRequisiteIndex = (state: RootState): number =>
  state.nodes.selectedRequisiteIndex;

export const selectConditionRules = createSelector(
  selectSelectedCondition,
  (condition): Condition['rules'] => condition?.rules ?? []
);

export const selectFirstActionIdByNodeId = (nodeId: string) =>
  createSelector(
    (state: RootState) => state.nodes.nodes,
    (nodes) => {
      const node = nodes.find((n) => n.node_id === nodeId);

      if (node) {
        return node.actions?.[0]?.action_id;
      }
      return null;
    }
  );

export const selectNodeIdByActionId = (actionId: string) =>
  createSelector(
    (state: RootState) => selectNodeByActionId(state, actionId),
    (node) => node?.node_id
  );

const selectActionId = (state: RootState) => selectSelectedAction(state).action_id;

// Type guard helper function
/**
 * Type guard to check if an action is a TextAction
 * @param action - The action to check
 * @returns Boolean indicating if the action is a TextAction
 */
const isTextAction = (action): action is TextAction => 'texts' in action;

/**
 * Selects texts from the currently selected action
 */
const selectTexts = createSelector([selectSelectedAction], (selectedAction) => {
  if (isTextAction(selectedAction)) {
    return selectedAction.texts || [];
  }
  return [];
});

/**
 * Selects options from the currently selected action
 */
const selectOptions = createSelector([selectSelectedAction], (selectedAction) => {
  if (isTextAction(selectedAction)) {
    return selectedAction.options || [];
  }
  return [];
});

/**
 * Selects passthrough data from the currently selected action
 */
export const selectPassthrough = createSelector([selectSelectedAction], (selectedAction) => {
  if (selectedAction.type === 'text' || selectedAction.type === 'carousel') {
    return selectedAction.passthrough || [];
  }
  return undefined;
});

/**
 * Combines action data including ID, texts, options, and passthrough
 */
export const selectActionData = createSelector(
  selectActionId,
  selectTexts,
  selectOptions,
  (actionId, texts, options) => ({ actionId, texts, options })
);

// Selector to get nodeId from the state, assuming you have a way to get it
const getNodeId = (state, nodeId) => nodeId;

// Selector to get the selected node
const getNode = createSelector([getNodeId, (state) => state], (nodeId, state) => {
  return selectNodeById(state, nodeId);
});

// Selector for actions
const getActions = createSelector([getNode], (node) => node?.actions || []);

// Selector for conditions
const getConditions = createSelector([getNode], (node) => node?.conditions || []);

// Selector to determine if the node is selected
const getIsSelected = createSelector([getNodeId, (state) => state.nodes], (nodeId, nodesState) => {
  return (
    nodeId === nodesState.selectedNodeId &&
    nodesState.selectedRequisiteIndex === null &&
    nodesState.selectedConditionIndex === null
  );
});

// Selector for last action type
const getLastActionType = createSelector(
  [getActions],
  (actions) => actions[actions.length - 1]?.type
);

// Selector to determine if the root node is unknown
const getIsRootNodeUnknown = createSelector(
  [getNode, (state) => state.nodes.nodes],
  (node, nodes) => {
    return nodes[0]?.type === 'unknown' && !node?.parent_id;
  }
);

// Combined selector
/**
 * Combined selector for node-related data
 * @returns Object containing node data and derived states
 */
export const nodeSelector = createSelector(
  [getNode, getActions, getConditions, getIsSelected, getLastActionType, getIsRootNodeUnknown],
  (node, actions, conditions, isSelected, lastActionType, isRootNodeUnknown) => {
    return {
      name: node?.name,
      type: node?.type,
      parentId: node?.parent_id,
      showPlaceholder: actions.length === 0 && conditions.length === 0,
      isSelected,
      intent: node?.intent,
      node,
      lastActionType,
      hasActions: actions.length > 0,
      isRootNodeUnknown,
      hasConditions: conditions.length > 0,
    };
  }
);
